import type { IntakeSpec, MechanicalFixtureDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): MechanicalFixtureDetails {
  return intake.details as MechanicalFixtureDetails;
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const wp = d.workpieceMax;
  const bpWidth = wp.length + 100;
  const bpDepth = wp.width + 100;
  const bpThickness = 15;

  const out: Part[] = [
    {
      id: 'baseplate',
      name: 'ベースプレート',
      kind: 'panel',
      materialId: 'mat-primary',
      width: bpWidth,
      depth: bpDepth,
      thickness: bpThickness,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['baseplate'],
      paramRefs: [],
      cutouts: [],
    },
  ];

  if (d.hasAlignmentPins) {
    out.push(
      {
        id: 'alignment_pin_1',
        name: '位置決めピン 1',
        kind: 'custom',
        materialId: 'mat-primary',
        openscadSnippet: '// alignment pin cylinder stub',
        boundingBox: { w: 6, d: 6, h: 30 },
        position: [-wp.length / 2, -wp.width / 2, bpThickness],
        rotation: [0, 0, 0],
        tags: ['alignment_pin'],
        paramRefs: [],
      },
      {
        id: 'alignment_pin_2',
        name: '位置決めピン 2',
        kind: 'custom',
        materialId: 'mat-primary',
        openscadSnippet: '// alignment pin cylinder stub',
        boundingBox: { w: 6, d: 6, h: 30 },
        position: [wp.length / 2, wp.width / 2, bpThickness],
        rotation: [0, 0, 0],
        tags: ['alignment_pin'],
        paramRefs: [],
      },
    );
  }

  out.push({
    id: 'clamp_element',
    name: 'クランプ要素',
    kind: 'custom',
    materialId: 'mat-primary',
    openscadSnippet: '// clamping element placeholder',
    boundingBox: { w: 80, d: 40, h: 60 },
    position: [bpWidth / 2 - 40, 0, bpThickness],
    rotation: [0, 0, 0],
    tags: ['clamp'],
    paramRefs: [],
  });

  return out;
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const wp = d.workpieceMax;
  const bpWidth = wp.length + 100;
  const bpDepth = wp.width + 100;
  const bpThickness = 15;
  const out: Feature[] = [];

  if (d.baseplateMounting === 'tslot') {
    const slotPositions: Array<[number, number, number]> = [
      [-(bpWidth / 2 - 20), -(bpDepth / 2 - 20), 0],
      [bpWidth / 2 - 20, -(bpDepth / 2 - 20), 0],
      [-(bpWidth / 2 - 20), bpDepth / 2 - 20, 0],
      [bpWidth / 2 - 20, bpDepth / 2 - 20, 0],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `tslot_${i + 1}`,
        partId: 'baseplate',
        kind: 'slot',
        length: 80,
        width: 8,
        depth: bpThickness,
        axis: 'z',
        through: true,
        roundedEnds: true,
        position: slotPositions[i],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
    }
  } else if (d.baseplateMounting === 'screw_down') {
    const screwPositions: Array<[number, number, number]> = [
      [-(bpWidth / 2 - 20), -(bpDepth / 2 - 20), 0],
      [bpWidth / 2 - 20, -(bpDepth / 2 - 20), 0],
      [-(bpWidth / 2 - 20), bpDepth / 2 - 20, 0],
      [bpWidth / 2 - 20, bpDepth / 2 - 20, 0],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `mount_hole_${i + 1}`,
        partId: 'baseplate',
        kind: 'screw_hole',
        size: 'M6',
        length: bpThickness,
        head: 'flat',
        axis: 'z',
        thread: false,
        holeOversize: 0.4,
        teardrop: false,
        position: screwPositions[i],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
    }
  } else if (d.baseplateMounting === 'magnetic') {
    // No holes needed for magnetic mounting
  }

  // Edge chamfer on baseplate (manufacturing/extreme)
  out.push({
    id: 'baseplate_chamfer',
    partId: 'baseplate',
    kind: 'edge_treatment',
    treatment: 'chamfer',
    amount: 1,
    scope: 'all',
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    paramRefs: [],
  });

  return out;
}
